import {
  Map,
  OrderedMap,
  List,
  fromJS,
  Map as ImmutableMap,
  OrderedMap as ImmutableOrderedMap,
  List as ImmutableList,
} from 'immutable';
import WorkspaceMeetingModeConstants from 'Shared/constants/WorkspaceMeetingMode.constants';
import type { AnyAction } from 'redux';
import { BotAttributes } from 'Shared/types/WorkspaceMeetingMode.type';

// The isMergable and mergeDeepOverwriteLists functions come from this Github issue where
// someone was having an issue with mergeDeep concating arrays together:
// https://github.com/TehShrike/deepmerge/issues/90
// It should act just as mergeDeep does, but it will always overwrite arrays rather than
// trying to join them together into one.
const isMergeable = (a: any): boolean =>
  a && typeof a === 'object' && typeof a.mergeWith === 'function' && !List.isList(a);

export const mergeDeepOverwriteLists = (a: any, b: any): any => {
  if (b === null) return b;
  if (isMergeable(a) && !List.isList(a)) {
    return a.mergeWith(mergeDeepOverwriteLists, b);
  }
  return b;
};

export interface WorkspaceMeetingModeState extends ImmutableMap<string, any> {
  WORKSPACE_MEETING_MODES_BY_WORKSPACE_ID: ImmutableOrderedMap<string, any>;
  DISMISSED_PARTICIPANT_POPOVER_MEET_MODE_ID: ImmutableList<any>;
  WORKSPACE_BOTS: ImmutableMap<string, BotAttributes>;
}

const initialState: WorkspaceMeetingModeState = Map({
  [WorkspaceMeetingModeConstants.State.WORKSPACE_MEETING_MODES_BY_WORKSPACE_ID]: OrderedMap(),
  [WorkspaceMeetingModeConstants.State.DISMISSED_PARTICIPANT_POPOVER_MEET_MODE_ID]: List(),
  [WorkspaceMeetingModeConstants.State.WORKSPACE_BOTS]: Map(),
}) as WorkspaceMeetingModeState;

export const WorkspaceMeetingModeReducer = (
  state: WorkspaceMeetingModeState = initialState,
  action: AnyAction
): WorkspaceMeetingModeState => {
  switch (action.type) {
    case WorkspaceMeetingModeConstants.Actions.FETCH_MEET_MODE_SUCCESS:
    case WorkspaceMeetingModeConstants.Actions.START_MEET_MODE_SUCCESS:
    case WorkspaceMeetingModeConstants.Actions.UPDATE_MEET_MODE_SUCCESS:
    case WorkspaceMeetingModeConstants.Actions.ACTION_CABLE_UPDATE_SUCCESS:
      return state.updateIn(
        [WorkspaceMeetingModeConstants.State.WORKSPACE_MEETING_MODES_BY_WORKSPACE_ID],
        (existing: ImmutableOrderedMap<string, any> = OrderedMap()) =>
          existing.merge(
            OrderedMap(
              Object.values(action.payload.meetingModeSession).map((item: any) => {
                if (existing.has(item.attributes.workspaceSlug)) {
                  return [
                    item.attributes.workspaceSlug,
                    mergeDeepOverwriteLists(existing.get(item.attributes.workspaceSlug), Map(fromJS(item.attributes))),
                  ];
                }
                return [item.attributes.workspaceSlug, Map(fromJS(item.attributes))];
              })
            )
          )
      ) as WorkspaceMeetingModeState;
    case WorkspaceMeetingModeConstants.Actions.LEAVE_MEET_MODE_SUCCESS:
      return state.deleteIn([
        WorkspaceMeetingModeConstants.State.WORKSPACE_MEETING_MODES_BY_WORKSPACE_ID,
        action.payload,
      ]) as WorkspaceMeetingModeState;
    case WorkspaceMeetingModeConstants.Actions.DISMISS_PARTICIPANT_POPOVER_SUCCESS:
      return state.updateIn(
        [WorkspaceMeetingModeConstants.State.DISMISSED_PARTICIPANT_POPOVER_MEET_MODE_ID],
        (existing: ImmutableList<any> = List()) => existing.insert(0, action.payload)
      ) as WorkspaceMeetingModeState;
    case WorkspaceMeetingModeConstants.Actions.INVITE_BOT_SUCCESS:
    case WorkspaceMeetingModeConstants.Actions.REMOVE_BOT_SUCCESS:
    case WorkspaceMeetingModeConstants.Actions.FETCH_BOT_SUCCESS: {
      const { data } = action.payload;
      if (!data?.attributes?.workspaceSlug) {
        return state;
      }
      return state.updateIn(
        [WorkspaceMeetingModeConstants.State.WORKSPACE_BOTS],
        (invites: ImmutableMap<string, BotAttributes> = Map()) =>
          invites.set(data.attributes.workspaceSlug, fromJS(data.attributes))
      ) as WorkspaceMeetingModeState;
    }
    default:
      return state;
  }
};
